"use client";

import { gsap, useGSAP, ScrollTrigger } from "@/shared/lib/gsap";
import { useRef } from "react";
import { useTranslations } from "next-intl";
import { useBreakpoint } from "@/shared/hooks/use-breakpoint";

// 모바일 전용 애니메이션
function MobileCircleMotion() {
  const circle1Ref = useRef<HTMLDivElement>(null);
  const circle2Ref = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const t = useTranslations();

  useGSAP(
    () => {
      if (!containerRef.current || !circle1Ref.current || !circle2Ref.current) return;

      // 자신의 애니메이션만 정리
      gsap.killTweensOf([circle1Ref.current, circle2Ref.current]);

      // 초기값 설정
      gsap.set([circle1Ref.current, circle2Ref.current], { x: 0, y: 0 });

      const animation = gsap.timeline();

      // 모바일: 위아래 원들이 가운데로 이동하여 84px 겹치도록
      animation.to(circle1Ref.current, {
        duration: 5,
        y: 108, // 위쪽 원이 아래로 이동
        ease: "power2.inOut",
      });

      animation.to(
        circle2Ref.current,
        {
          duration: 5,
          y: -108, // 아래쪽 원이 위로 이동
          ease: "power2.inOut",
        },
        "<", // 동시에 실행
      );

      ScrollTrigger.create({
        animation,
        trigger: containerRef.current,
        start: "top bottom",
        end: "bottom 70%",
        scrub: true,
      });
    },
    { dependencies: [] },
  );

  return (
    <div ref={containerRef} className="relative mt-[33px] flex w-full flex-col items-center justify-center gap-[40px]">
      <div
        className="flex h-[260px] w-[260px] shrink-0 items-center justify-center rounded-full border border-white/50 bg-white/20 text-center"
        ref={circle1Ref}
      >
        <span className="heading-3-medium w-60 tracking-[-0.03em] whitespace-pre-line text-white">
          {t("mn_pd_pn_1_context_1")}
        </span>
      </div>

      <div className="z-10 flex h-[260px] w-[260px] shrink-0 items-center justify-center rounded-full border border-white/50 bg-white text-center">
        <span className="heading-3-bold w-60 tracking-[-0.03em] whitespace-pre-line text-gray-900">
          {t("mn_pd_pn_1_context_2")}
        </span>
      </div>

      <div
        className="flex h-[260px] w-[260px] shrink-0 items-center justify-center rounded-full border border-white/50 bg-white/20 text-center"
        ref={circle2Ref}
      >
        <span className="heading-3-medium w-60 tracking-[-0.03em] whitespace-pre-line text-white">
          {t("mn_pd_pn_1_context_3")}
        </span>
      </div>
    </div>
  );
}

// PC 전용 애니메이션
function DesktopCircleMotion() {
  const circle1Ref = useRef<HTMLDivElement>(null);
  const circle2Ref = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const t = useTranslations();

  useGSAP(
    () => {
      if (!containerRef.current || !circle1Ref.current || !circle2Ref.current) return;

      // 자신의 애니메이션만 정리
      gsap.killTweensOf([circle1Ref.current, circle2Ref.current]);

      // 초기값 설정
      gsap.set([circle1Ref.current, circle2Ref.current], { x: 0, y: 0 });

      const animation = gsap.timeline();

      // PC: 좌우 원들이 가운데로 이동하여 겹치도록
      animation.to(circle1Ref.current, {
        duration: 5,
        x: 240, // 오른쪽 원이 왼쪽으로 이동
        ease: "power2.inOut",
      });

      animation.to(
        circle2Ref.current,
        {
          duration: 5,
          x: -240, // 왼쪽 원이 오른쪽으로 이동
          ease: "power2.inOut",
        },
        "<", // 동시에 실행
      );

      ScrollTrigger.create({
        animation,
        trigger: containerRef.current,
        start: "top bottom",
        end: "bottom 70%",
        scrub: true,
      });
    },
    { dependencies: [] },
  );

  return (
    <div ref={containerRef} className="relative mt-13 flex w-full flex-row items-center justify-center gap-[200px]">
      <div
        className="flex h-[380px] w-[380px] shrink-0 items-center justify-center rounded-full border border-[#DBDBDB80]/50 bg-white/20 text-center"
        ref={circle1Ref}
      >
        <span className="heading-3-medium w-70 tracking-[-0.03em] text-white">{t("mn_pd_pn_1_context_1")}</span>
      </div>

      <div className="z-10 flex h-[380px] w-[380px] shrink-0 items-center justify-center rounded-full border border-[#DBDBDB80]/50 bg-white text-center">
        <span className="heading-3-bold w-70 tracking-[-0.03em] text-gray-900">{t("mn_pd_pn_1_context_2")}</span>
      </div>

      <div
        className="flex h-[380px] w-[380px] shrink-0 items-center justify-center rounded-full border border-[#DBDBDB80]/50 bg-white/20 text-center"
        ref={circle2Ref}
      >
        <span className="heading-3-medium w-70 tracking-[-0.03em] text-white">{t("mn_pd_pn_1_context_3")}</span>
      </div>
    </div>
  );
}

function TabletCircleMotion() {
  const circle1Ref = useRef<HTMLDivElement>(null);
  const circle2Ref = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const t = useTranslations();

  useGSAP(
    () => {
      if (!containerRef.current || !circle1Ref.current || !circle2Ref.current) return;

      // 자신의 애니메이션만 정리
      gsap.killTweensOf([circle1Ref.current, circle2Ref.current]);

      // 초기값 설정
      gsap.set([circle1Ref.current, circle2Ref.current], { x: 0, y: 0 });

      const animation = gsap.timeline();

      // PC: 좌우 원들이 가운데로 이동하여 겹치도록
      animation.to(circle1Ref.current, {
        duration: 5,
        x: 120, // 오른쪽 원이 왼쪽으로 이동
        ease: "power2.inOut",
      });

      animation.to(
        circle2Ref.current,
        {
          duration: 5,
          x: -120, // 왼쪽 원이 오른쪽으로 이동
          ease: "power2.inOut",
        },
        "<", // 동시에 실행
      );

      ScrollTrigger.create({
        animation,
        trigger: containerRef.current,
        start: "top bottom",
        end: "bottom 70%",
        scrub: true,
      });
    },
    { dependencies: [] },
  );

  return (
    <div ref={containerRef} className="relative mt-13 flex w-full flex-row items-center justify-center gap-[100px]">
      <div
        className="flex h-[270px] w-[270px] shrink-0 items-center justify-center rounded-full border border-[#DBDBDB80]/50 bg-white/20 text-center"
        ref={circle1Ref}
      >
        <span className="heading-3-medium whitespace-pre-line text-white">{t("mn_pd_pn_1_context_1")}</span>
      </div>

      <div className="z-10 flex h-[270px] w-[270px] shrink-0 items-center justify-center rounded-full border border-[#DBDBDB80]/50 bg-white text-center">
        <span className="heading-3-bold whitespace-pre-line text-gray-900">{t("mn_pd_pn_1_context_2")}</span>
      </div>

      <div
        className="flex h-[270px] w-[270px] shrink-0 items-center justify-center rounded-full border border-[#DBDBDB80]/50 bg-white/20 text-center"
        ref={circle2Ref}
      >
        <span className="heading-3-medium whitespace-pre-line text-white">{t("mn_pd_pn_1_context_3")}</span>
      </div>
    </div>
  );
}

export function ScrollCircleMotion() {
  const { isMobile, isTablet } = useBreakpoint();

  return isMobile ? <MobileCircleMotion /> : isTablet ? <TabletCircleMotion /> : <DesktopCircleMotion />;
}
